import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { logout, fetchUser } from "@/modules/auth/store/authSlice";
import { useTranslation } from "react-i18next";
import { BellIcon, ChevronDown, MoreVertical } from "lucide-react";
import {
  selectUnreadByUser,
  markAsRead,
} from "@/modules/notifications/store/notificationsSlice";
import {
  resolveNotificationTitle,
  resolveNotificationTargetRoute,
} from "../utils/notificationUtils";
import ThemeSwitcher from "./ThemeSwitcher";
import { getServiceModuleName, matchModuleByPath } from "../config/serviceModules";
import { buildNavbarMenusByKeyFromManifests } from "@/modules/manifestRegistry";

export default function Navbar() {
  const { i18n, t } = useTranslation();
  const user = useSelector((s) => s?.auth?.user);

  // Public website links are only enabled when explicitly pointed at the production site.
  // (Vite only exposes env vars prefixed with VITE_ by default.)
  const publicSiteUrl = (() => {
    const raw =
      import.meta.env?.VITE_PUBLIC_SITE_URL ||
      import.meta.env?.VITE_Public_site_URL ||
      import.meta.env?.PUBLIC_SITE_URL ||
      import.meta.env?.Public_site_URL ||
      "";
    return String(raw || "")
      .trim()
      .replace(/\/+$/, "");
  })();
  const publicLinksEnabled = publicSiteUrl === "https://tarhplatform.com";
  const publicLink = (path) => (publicLinksEnabled ? `${publicSiteUrl}${path}` : "#");

  const otpPending = (() => {
    try {
      return localStorage.getItem("otp_pending") === "1";
    } catch (_) {
      return false;
    }
  })();
  const dispatch = useDispatch();
  const location = useLocation();
  const subscribed = useSelector((s) => s.subscriptions?.subscribed);
  const navigate = useNavigate();
  const currentLang = i18n.language;
  const activeModule = React.useMemo(
    () => (user ? matchModuleByPath(location.pathname) : null),
    [location.pathname, user]
  );
  // const notifications = useSelector((s) => s?.notifications?.items);
  const unread = useSelector((s) => selectUnreadByUser(s, user?.uuid));
  const documentVerified = user?.document_verified !== false;

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileDropdownOpen, setIsMobileDropdownOpen] = useState(false);
  const [nfDropdownOpen, setNfDropdownOpen] = useState(false);
  const [openMenuKey, setOpenMenuKey] = useState(null);
  const [mobileOpenMenuKey, setMobileOpenMenuKey] = useState(null);
  const dropdownRef = useRef(null);
  const menuCloseTimerRef = useRef(null);
  const desktopMenuViewportRef = useRef(null);
  const desktopMenuMeasureItemRefs = useRef([]);
  const desktopMenuMeasureMoreRef = useRef(null);
  const [desktopVisibleMenuCount, setDesktopVisibleMenuCount] = useState(0);

  const unreadCount = documentVerified ? unread?.length || 0 : 0;

  const handlemarkAsRead = async (id) => {
    dispatch(markAsRead({ id, userUuid: user.uuid }));
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".notification-wrapper")) {
        setNfDropdownOpen((prev) => (prev ? false : prev));
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".menu-dropdown-wrapper")) {
        setOpenMenuKey((prev) => (prev === null ? prev : null));
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setOpenMenuKey((prev) => (prev === null ? prev : null));
      setIsDropdownOpen((prev) => (prev ? false : prev));
      setNfDropdownOpen((prev) => (prev ? false : prev));
      setIsMobileDropdownOpen((prev) => (prev ? false : prev));
      setMobileOpenMenuKey((prev) => (prev === null ? prev : null));
      setIsMobileMenuOpen((prev) => (prev ? false : prev));
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const openMenu = (key) => {
    if (menuCloseTimerRef.current) {
      clearTimeout(menuCloseTimerRef.current);
      menuCloseTimerRef.current = null;
    }
    setOpenMenuKey(key);
  };

  const closeMenuSoon = () => {
    if (menuCloseTimerRef.current) {
      clearTimeout(menuCloseTimerRef.current);
    }
    menuCloseTimerRef.current = setTimeout(() => {
      setOpenMenuKey(null);
      menuCloseTimerRef.current = null;
    }, 150);
  };

  useEffect(() => {
    return () => {
      if (menuCloseTimerRef.current) {
        clearTimeout(menuCloseTimerRef.current);
        menuCloseTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    setOpenMenuKey((prev) => (prev === null ? prev : null));
  }, [location.pathname]);

  // Defensive: when language changes, force-close any open dropdown state so we
  // don't end up with mixed-language menu snapshots during fast toggles.
  useEffect(() => {
    setOpenMenuKey((prev) => (prev === null ? prev : null));
    setMobileOpenMenuKey((prev) => (prev === null ? prev : null));
    setIsDropdownOpen((prev) => (prev ? false : prev));
    setIsMobileDropdownOpen((prev) => (prev ? false : prev));
    setNfDropdownOpen((prev) => (prev ? false : prev));
  }, [currentLang]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden"; // lock background scroll
    } else {
      document.body.style.overflow = "";
    }
  }, [isMobileMenuOpen]);

  const navItems = React.useMemo(
    () => [
      { label: t("aboutTarh"), path: publicLink("/") },
      {
        label: t("electronicServices"),
        path: publicLink("/services"),
      },
      {
        label: t("guidanceLibrary"),
        path: publicLink("/helpcenter"),
      },
      { label: t("contactUs"), path: publicLink("/contact") },
    ],
    [publicLinksEnabled, publicSiteUrl, t]
  );

  const isDesktop = typeof window !== "undefined" && window.innerWidth >= 1024;
  const ALL_TENDERS_ENABLED = true;
  const COMPANY_LIST_ENABLED = false;
  const isAuthorizedOnly =
    !!user &&
    (user.role === "authorized_user" ||
      user.auth_context === "authorized_user");

  const hasPermission = (permissions = [], mode = "any") => {
    if (!isAuthorizedOnly) return true;
    const list = Array.isArray(permissions)
      ? permissions.filter(Boolean)
      : permissions
        ? [permissions]
        : [];
    if (list.length === 0) return true;
    const check = (key) => !!user?.[`can_${key}`];
    return mode === "all" ? list.every(check) : list.some(check);
  };

  const applyMenuPermissions = (menu) => {
    const permissionList = menu.permissions || [];
    const permissionMode = menu.permissionMode || "any";
    const permissionBlocked =
      permissionList.length > 0 && !hasPermission(permissionList, permissionMode);
    const disabled = !!menu.disabled || permissionBlocked;
    const disabledReason =
      menu.disabledReason || (permissionBlocked ? "permission" : undefined);
    const children = Array.isArray(menu.children)
      ? menu.children.map(applyMenuPermissions)
      : menu.children;
    return { ...menu, disabled, disabledReason, children };
  };

  const servicesHomeMenu = React.useMemo(
    () => ({
      label: t("services_home_label"),
      path: "/dashboard",
    }),
    [t]
  );
  const activeModuleLabel =
    activeModule?.key === "offers_management" ||
      activeModule?.key === "tenders_offers_management" || activeModule?.key === "financial_claims_management" || activeModule?.key ===
      "approvals_requests_management" || activeModule?.key === "proposal_approvals_management" || activeModule?.key === "delegation_permissions_management" || activeModule?.key === "negotiation_award_management"
      ? t("dashboard")
      : activeModule
        ? getServiceModuleName(activeModule, currentLang, "dashboard")
        : "";


  const moduleMenusByKey = React.useMemo(
    () =>
      buildNavbarMenusByKeyFromManifests({
        t,
        activeModuleLabel,
        ALL_TENDERS_ENABLED,
        COMPANY_LIST_ENABLED,
        isAuthorizedOnly,
      }),
    [ALL_TENDERS_ENABLED, COMPANY_LIST_ENABLED, activeModuleLabel, isAuthorizedOnly, t]
  );

  const expandedActiveModuleMenus = React.useMemo(() => {
    if (!activeModule) {
      return [];
    }

    const moduleMenus = moduleMenusByKey[activeModule.key] || [];
    const hasDeclaredDefaultPath = moduleMenus.some((item) => {
      if (item?.path === activeModule.defaultPath) return true;
      if (!Array.isArray(item?.children)) return false;
      return item.children.some((child) => child?.path === activeModule.defaultPath);
    });

    const flattened = hasDeclaredDefaultPath
      ? []
      : [
        {
          label: activeModuleLabel,
          path: activeModule.defaultPath,
        },
      ];

    moduleMenus.forEach((item) => {
      if (Array.isArray(item.children) && item.children.length > 0) {
        flattened.push(...item.children);
        return;
      }

      flattened.push(item);
    });

    const seen = new Set();
    return flattened.filter((item) => {
      const key = item.path;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }, [activeModule, activeModuleLabel, moduleMenusByKey]);

  const authenticatedMenus = React.useMemo(() => {
    if (!user || otpPending) {
      return navItems;
    }
    if (!activeModule) {
      return navItems;
    }
    const hasDashboardEntry = expandedActiveModuleMenus.some(
      (item) => item?.path === "/dashboard"
    );
    if (hasDashboardEntry) {
      return expandedActiveModuleMenus;
    }
    return [servicesHomeMenu, ...expandedActiveModuleMenus];
  }, [activeModule, expandedActiveModuleMenus, navItems, otpPending, servicesHomeMenu, user]);

  const permissionAwareMenus = React.useMemo(
    () => authenticatedMenus.map(applyMenuPermissions),
    [authenticatedMenus]
  );

  const filteredMenus = React.useMemo(
    () =>
      permissionAwareMenus.filter((menu) => {
        if (location.pathname === "/launch") return false;
        if (menu.path === "/notifications" && isDesktop && !activeModule)
          return false;
        if (menu.path === "/notifications" && user && !documentVerified)
          return false;

        return true;
      }),
    [activeModule, documentVerified, isDesktop, location.pathname, permissionAwareMenus, user]
  );

  // Guardrail: ensure we never render a mixed public+module navbar row.
  // Public navbar items are external URLs/#, module navbar items are internal routes (/...).
  const isModuleNavbarMode = !!user && !otpPending && !!activeModule;
  const navbarMenus = React.useMemo(() => {
    const scopedMenus = isModuleNavbarMode
      ? filteredMenus.filter(
        (menu) =>
          (typeof menu?.path === "string" && menu.path.startsWith("/")) ||
          (Array.isArray(menu?.children) && menu.children.length > 0)
      )
      : filteredMenus.filter(
        (menu) => typeof menu?.path === "string" && !menu.path.startsWith("/")
      );

    const seen = new Set();
    return scopedMenus.filter((menu) => {
      const key = `${menu.path}::${menu.label || ""}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [filteredMenus, isModuleNavbarMode]);
  const accountMenu = [
    // { label: t("dashboard"), path: "/dashboard" },

    { label: t("notifications"), path: "/notifications" },
    { label: t("profile"), path: "/profile" },
    {
      label: t("authorized_users"),
      path: "/delegates/permissions",
    },
    {
      label: t("field_of_work_classification_title"),
      path: "/field-of-work-classification",
    },
    { label: t("user_manual"), path: "http://tarhplatform.com/assets/%D9%84%D8%AF%D9%84%D9%8A%D9%84%20%D8%A7%D9%84%D8%AA%D8%B9%D8%B1%D9%8A%D9%81%D9%8A%20%D9%84%D9%85%D9%86%D8%B5%D8%A9%20%D8%B7%D8%B1%D8%AD.pdf", download: true },
    { label: t("contactUs"), path: publicLink("/contact") },
  ];

  const filteredAccountMenu = accountMenu.filter((menu) => {
    if (!subscribed) {
      return menu.path === publicLink("/contact");
    }
    if (
      location.pathname === "/launch" &&
      (menu.path === "/dashboard" || menu.path === "/notifications")
    ) {
      return false;
    }
    if (menu.path === "/notifications" && user && !documentVerified) {
      return false;
    }

    if (
      menu.path === "/delegates/permissions" &&
      (isAuthorizedOnly || !documentVerified)
    ) {
      return false;
    }

    if (
      menu.path === "/field-of-work-classification" &&
      (isAuthorizedOnly || !subscribed)
    ) {
      return false;
    }
    return true;
  });

  const logoTarget = user ? "/dashboard" : "/";

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen((prev) => (prev ? false : prev));
        // setIsMobileDropdownOpen(false);
        // setIsMobileMenuOpen(false);
        // setMoreOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownRef]);

  // On route changes, lightly re-check user profile so that
  // backend changes to document_verified (e.g. from true -> false)
  // are picked up without requiring a full page refresh.
  useEffect(() => {
    if (!user) return;
    if (user.document_verified === false) return;
    dispatch(fetchUser());
  }, [location.pathname, user?.uuid, dispatch]);

  // Toggle dropdown visibility
  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  // Handle language switching
  const switchLang = () => {
    const next = i18n.language === "en" ? "ar" : "en";
    i18n.changeLanguage(next);
    localStorage.setItem("lang", next);
    document.documentElement.setAttribute("dir", next === "ar" ? "rtl" : "ltr");
    document.documentElement.lang = next;
  };

  const linkClasses = (path) =>
    `transition-colors duration-200 font-bold
   px-0.5 py-1 mx-0.5 text-sm
   lg:px-1 lg:py-1.5 lg:mx-1 lg:text-base whitespace-nowrap
   ${location.pathname === path
      ? "text-green-700 dark:text-green-700 border-b-2 border-green-700"
      : "text-gray-700 dark:text-white hover:text-green-700 hover:border-green-700 border-b-2 border-transparent"
    }`;

  const getDisabledTitle = (reason) => {
    if (reason === "soon") return t("soon");
    return t("action_not_allowed") || "Action not allowed";
  };

  const shouldMeasureDesktopOverflow =
    isModuleNavbarMode && navbarMenus.length > 6;

  const desktopEffectiveVisibleCount = Math.max(
    0,
    Math.min(
      navbarMenus.length,
      shouldMeasureDesktopOverflow
        ? Number(desktopVisibleMenuCount) || 0
        : navbarMenus.length
    )
  );
  const desktopVisibleMenus = navbarMenus.slice(0, desktopEffectiveVisibleCount);
  const desktopOverflowMenus =
    desktopEffectiveVisibleCount < navbarMenus.length
      ? navbarMenus.slice(desktopEffectiveVisibleCount)
      : [];
  const desktopOverflowEntries = React.useMemo(() => {
    const out = [];
    for (const item of desktopOverflowMenus) {
      if (Array.isArray(item?.children) && item.children.length > 0) {
        out.push(...item.children);
        continue;
      }
      out.push(item);
    }
    return out;
  }, [desktopOverflowMenus]);

  React.useLayoutEffect(() => {
    if (!shouldMeasureDesktopOverflow) {
      return;
    }

    const viewport = desktopMenuViewportRef.current;
    if (!viewport) return;

    const recalc = () => {
      const el = desktopMenuViewportRef.current;
      if (!el) return;
      const containerWidth = el.clientWidth || 0;
      if (containerWidth <= 0) return;

      desktopMenuMeasureItemRefs.current = desktopMenuMeasureItemRefs.current.slice(
        0,
        navbarMenus.length
      );

      const measureNode = (node) => {
        if (!node) return 0;
        const rect = node.getBoundingClientRect();
        const style = window.getComputedStyle(node);
        const ml = parseFloat(style.marginLeft) || 0;
        const mr = parseFloat(style.marginRight) || 0;
        return (rect.width || 0) + ml + mr;
      };

      const widths = navbarMenus.map((_, idx) => {
        const node = desktopMenuMeasureItemRefs.current[idx];
        return measureNode(node);
      });

      const total = widths.reduce(
        (sum, w) => sum + (Number.isFinite(w) ? w : 0),
        0
      );
      if (total <= containerWidth) {
        setDesktopVisibleMenuCount((prev) =>
          prev === widths.length ? prev : widths.length
        );
        return;
      }

      const moreW = measureNode(desktopMenuMeasureMoreRef.current) || 44;

      // Buffer for margins/gaps so we never wrap into the logo/right area.
      const buffer = 20;
      const available = Math.max(0, containerWidth - moreW - buffer);

      let used = 0;
      let count = 0;
      for (let i = 0; i < widths.length; i++) {
        const w = widths[i] || 0;
        if (used + w <= available) {
          used += w;
          count += 1;
          continue;
        }
        break;
      }

      setDesktopVisibleMenuCount((prev) => (prev === count ? prev : count));
    };

    // Sync recalculation before paint so we never render a wrapped/overflowing row.
    recalc();

    let raf = 0;
    const schedule = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(recalc);
    };

    const ro = new ResizeObserver(schedule);
    ro.observe(viewport);
    window.addEventListener("resize", schedule);

    return () => {
      if (raf) cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener("resize", schedule);
    };
  }, [currentLang, navbarMenus, location.pathname, shouldMeasureDesktopOverflow, user?.uuid]);

  return (
    <>
      {/* Desktop Navigation */}
      <nav className="bg-white shadow-md fixed top-0 left-0 right-0 z-50 dark:bg-slate-800 dark:text-white hidden lg:block">
        <div className="mx-auto flex items-center justify-between gap-4 px-6 py-3">
          {/* Left - Logo */}
          <div className="flex items-center shrink-0">
            <Link to={logoTarget} className="flex items-center">
              <img
                src="/logo.png"
                alt="Tarh Logo"
                className="h-10 w-auto max-w-[120px]"
              />
            </Link>
          </div>

          {/* Center - Navigation */}
          <div
            className={`flex flex-1 min-w-0 items-center justify-center ${currentLang === "en" ? "pl-2" : "pr-2"
              }`}
          >
            <div
              ref={desktopMenuViewportRef}
              className="relative w-full max-w-full overflow-visible px-4"
              style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
            >
              {/* Measure strip (offscreen) to compute overflow -> visible vs. "More" */}
              <div
                className="absolute left-[-10000px] top-[-10000px] flex min-w-max items-center justify-center pointer-events-none opacity-0"
                aria-hidden="true"
                key={`desktop-menu-measure-${currentLang}`}
              >
                {navbarMenus.map((item, idx) =>
                  item.disabled ? (
                    <span
                      key={`measure-${item.path}-${idx}`}
                      ref={(node) => {
                        desktopMenuMeasureItemRefs.current[idx] = node;
                      }}
                      className={`${linkClasses(
                        item.path
                      )} cursor-not-allowed opacity-70 relative inline-flex items-center`}
                    >
                      <span className="pr-4">{item.label}</span>
                    </span>
                  ) : Array.isArray(item.children) ? (
                    <span
                      key={`measure-${item.path}-${idx}`}
                      ref={(node) => {
                        desktopMenuMeasureItemRefs.current[idx] = node;
                      }}
                      className="group transition-colors duration-200 font-bold px-0.5 py-1 mx-0.5 text-sm lg:px-1 lg:py-1.5 lg:mx-1 lg:text-base whitespace-nowrap text-gray-700 dark:text-white border-b-2 border-transparent inline-flex items-center gap-1"
                    >
                      <span>{item.label}</span>
                    </span>
                  ) : (
                    <span
                      key={`measure-${item.path}-${idx}`}
                      ref={(node) => {
                        desktopMenuMeasureItemRefs.current[idx] = node;
                      }}
                      className={linkClasses(item.path)}
                    >
                      {item.label}
                    </span>
                  )
                )}
                <span
                  ref={desktopMenuMeasureMoreRef}
                  className="group transition-colors duration-200 font-bold px-2 py-1 mx-0.5 text-sm lg:px-2 lg:py-1.5 lg:mx-1 lg:text-base whitespace-nowrap text-gray-700 dark:text-white border-b-2 border-transparent inline-flex items-center gap-1"
                >
                  <MoreVertical size={18} />
                </span>
              </div>

              <div
                key={`desktop-menu-row-${currentLang}`}
                className="mx-auto flex min-w-0 items-center justify-center gap-x-0 [&::-webkit-scrollbar]:hidden"
              >
                {desktopVisibleMenus.map((item, idx) =>
                  item.disabled ? (
                    <span
                      key={`menu-${idx}-${item.path}`}
                      className={`${linkClasses(
                        item.path
                      )} cursor-not-allowed opacity-70 relative inline-flex items-center`}
                      title={getDisabledTitle(item.disabledReason)}
                    >
                      <span className="pr-4">
                        {item.label}{" "}
                        {item.disabledReason === "soon" && (
                          <span
                            className={`absolute -top-2 text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 border border-gray-300 uppercase tracking-wide ${currentLang === "en" ? "right-0" : "left-0"
                              }`}
                          >
                            {t("soon")}
                          </span>
                        )}
                      </span>
                    </span>
                  ) : Array.isArray(item.children) ? (
                    <div
                      key={`menu-${idx}-${item.path}`}
                      className="relative menu-dropdown-wrapper z-50"
                      onMouseEnter={() => openMenu(item.path)}
                      onMouseLeave={closeMenuSoon}
                    >
                      <button
                        type="button"
                        onClick={() => {
                          if (menuCloseTimerRef.current) {
                            clearTimeout(menuCloseTimerRef.current);
                            menuCloseTimerRef.current = null;
                          }
                          setOpenMenuKey((prev) => (prev === item.path ? null : item.path));
                        }}
                        onFocus={() => openMenu(item.path)}
                        className="group transition-colors duration-200 font-bold px-0.5 py-1 mx-0.5 text-sm lg:px-1 lg:py-1.5 lg:mx-1 lg:text-base whitespace-nowrap text-gray-700 dark:text-white hover:text-green-700 hover:border-green-700 border-b-2 border-transparent inline-flex items-center gap-1"
                      >
                        <span>{item.label}</span>
                        <ChevronDown
                          size={16}
                          className={`opacity-0 group-hover:opacity-100 transition-all duration-200 ${openMenuKey === item.path ? "rotate-180 opacity-100" : ""
                            }`}
                        />
                      </button>

                      {openMenuKey === item.path && (
                        <div
                          className={`menuwraper absolute ${currentLang === "en" ? "right-0" : "left-0"
                            } top-full mt-4 w-56 bg-white border border-gray-300 rounded-md shadow-lg z-50`}
                          onMouseEnter={() => openMenu(item.path)}
                          onMouseLeave={closeMenuSoon}
                        >
                          {(item.children || []).map((child) =>
                            child.disabled ? (
                              <span
                                key={child.path}
                                className="transition-colors duration-200 font-bold text-sm block px-4 py-2 text-sm text-gray-400 cursor-not-allowed"
                                title={getDisabledTitle(child.disabledReason)}
                              >
                                {child.label}
                                {child.disabledReason === "soon" && (
                                  <span className="ml-1 text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 border border-gray-300 uppercase tracking-wide">
                                    {t("soon")}
                                  </span>
                                )}
                              </span>
                            ) : (
                              <Link
                                key={child.path}
                                to={child.path}
                                onClick={() => setOpenMenuKey(null)}
                                className="transition-colors duration-200 font-bold text-sm block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                              >
                                {child.label}
                              </Link>
                            )
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <Link
                      key={`menu-${idx}-${item.path}`}
                      to={item.path}
                      className={linkClasses(item.path)}
                    >
                      {item.label}
                    </Link>
                  )
                )}
                {desktopOverflowEntries.length > 0 && (
                  <div
                    className="relative menu-dropdown-wrapper z-50"
                    onMouseEnter={() => openMenu("__more_overflow__")}
                    onMouseLeave={closeMenuSoon}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        if (menuCloseTimerRef.current) {
                          clearTimeout(menuCloseTimerRef.current);
                          menuCloseTimerRef.current = null;
                        }
                        setOpenMenuKey((prev) =>
                          prev === "__more_overflow__" ? null : "__more_overflow__"
                        );
                      }}
                      onFocus={() => openMenu("__more_overflow__")}
                      className="group transition-colors duration-200 font-bold px-2 py-1 mx-0.5 text-sm lg:px-2 lg:py-1.5 lg:mx-1 lg:text-base whitespace-nowrap text-gray-700 dark:text-white hover:text-green-700 hover:border-green-700 border-b-2 border-transparent inline-flex items-center"
                      aria-label={t("more") || "More"}
                    >
                      <MoreVertical size={18} />
                    </button>

                    {openMenuKey === "__more_overflow__" && (
                      <div
                        className={`menuwraper absolute ${currentLang === "en" ? "right-0" : "left-0"
                          } top-full mt-4 w-56 bg-white border border-gray-300 rounded-md shadow-lg z-50`}
                        onMouseEnter={() => openMenu("__more_overflow__")}
                        onMouseLeave={closeMenuSoon}
                      >
                        {desktopOverflowEntries.map((child, idx) =>
                          child.disabled ? (
                            <span
                              key={`more-${idx}-${child.path}`}
                              className="transition-colors duration-200 font-bold text-sm block px-4 py-2 text-sm text-gray-400 cursor-not-allowed"
                              title={getDisabledTitle(child.disabledReason)}
                            >
                              {child.label}
                              {child.disabledReason === "soon" && (
                                <span className="ml-1 text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 border border-gray-300 uppercase tracking-wide">
                                  {t("soon")}
                                </span>
                              )}
                            </span>
                          ) : (
                            <Link
                              key={`more-${idx}-${child.path}`}
                              to={child.path}
                              onClick={() => setOpenMenuKey(null)}
                              className="transition-colors duration-200 font-bold text-sm block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            >
                              {child.label}
                            </Link>
                          )
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right - Lang + Auth */}
          <div className="flex flex-nowrap items-center gap-x-1 shrink-0">
            {/* Language Switch */}
            <button
              onClick={switchLang}
              className="flex items-center gap-1 px-2 py-1 mx-1 rounded focus:outline-none hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <span
                className="text-lg font-bold"
                style={{ fontFamily: "Tajawal', sans-serif !important" }}
              >
                {currentLang === "ar" ? "EN" : "عربي"}
              </span>
            </button>

            {/* <ThemeSwitcher /> */}

            {user ? (
              <>
                {documentVerified && (
                  <div className="relative inline-block">
                    {location.pathname !== "/launch" && (
                      <button
                        onClick={() => setNfDropdownOpen(!nfDropdownOpen)}
                        className="relative p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-200 focus:outline-none"
                      >
                        <BellIcon
                          className={
                            unreadCount > 0
                              ? "text-black dark:text-white"
                              : "text-gray-400 dark:text-gray-400"
                          }
                        />
                        {unreadCount > 0 && (
                          <span className="absolute -top-1 -right-1 inline-flex items-center justify-center h-5 w-5 rounded-full bg-red-600 text-white text-xs font-bold shadow-md">
                            {unreadCount}
                          </span>
                        )}
                      </button>
                    )}

                    {nfDropdownOpen && (
                      <div
                        className={`notification-wrapper dark:bg-gray-800 dark:text-white absolute ${currentLang === "en" ? "right-0" : "left-0"
                          } mt-2 w-80 bg-white border border-gray-300 rounded-xl shadow-xl z-50`}
                      >
                        <div className="p-4">
                          <h1 className="text-base font-semibold text-gray-800 mb-3 border-b pb-2 flex justify-between items-center">
                            {t("notifications")}
                            <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-1 text-xs font-medium text-red-700">
                              {unreadCount > 0
                                ? `${unreadCount} ${t("unread")}`
                                : unreadCount}
                            </span>
                          </h1>

                          <div className="max-h-64 overflow-y-auto pr-1 space-y-3">
                            {unread?.length > 0 ? (
                              <>
                                {unread?.map((notification, index) => {
                                  const createdDate = new Date(
                                    notification.Creation_Date
                                  );
                                  const now = new Date();
                                  const diffTime = Math.abs(now - createdDate);
                                  const diffDays = Math.ceil(
                                    diffTime / (1000 * 60 * 60 * 24)
                                  );

                                  let timeAgo;
                                  if (diffDays === 1) {
                                    timeAgo = t("today") || "Today";
                                  } else if (diffDays === 2) {
                                    timeAgo = t("yesterday") || "Yesterday";
                                  } else if (diffDays <= 7) {
                                    timeAgo =
                                      t("daysAgoCount", { count: diffDays - 1 }) ||
                                      `${diffDays - 1} ${t("daysAgo") || "days ago"}`;
                                  } else {
                                    timeAgo = createdDate.toLocaleDateString(
                                      "en-US",
                                      {
                                        month: "short",
                                        day: "numeric",
                                      }
                                    );
                                  }

                                  const title = resolveNotificationTitle(
                                    notification,
                                    currentLang
                                  );
                                  const targetRoute =
                                    resolveNotificationTargetRoute(
                                      notification,
                                      user?.company_id || user?.company?.id || null
                                    );
                                  return (
                                    <Link
                                      to={targetRoute}
                                      key={notification.ID || index}
                                      className="block bg-white hover:bg-green-100 transition-colors duration-200 rounded-md p-2"
                                      onClick={() =>
                                        handlemarkAsRead(notification["ID"])
                                      }
                                    >
                                      <p className="text-sm text-gray-700 leading-snug">
                                        {title ? (
                                          <span
                                            style={{ whiteSpace: "pre-line" }}
                                          >
                                            {title}
                                          </span>
                                        ) : notification.Notification_Type ===
                                          "deadline reminder" ? (
                                          <>
                                            {t("NotificationTender") +
                                              " " +
                                              notification?.Tender_reference_number +
                                              " - " +
                                              t("NotificationTenderProject") +
                                              " " +
                                              notification?.TenderName +
                                              " " +
                                              t("high")}
                                          </>
                                        ) : (
                                          <>
                                            {t("NotificationTender") +
                                              " " +
                                              notification?.Tender_reference_number +
                                              " - " +
                                              t("NotificationTenderProject") +
                                              " " +
                                              notification?.TenderName +
                                              " " +
                                              t(
                                                "NotificationTenderOpenForOffer"
                                              )}
                                          </>
                                        )}
                                      </p>
                                      <div className="mt-1 text-xs text-gray-500">
                                        {timeAgo}
                                      </div>
                                    </Link>
                                  );
                                })}
                              </>
                            ) : (
                              <div className="text-center text-sm text-gray-500 py-4">
                                {t("noNotifications") ||
                                  "No active notifications"}
                              </div>
                            )}
                          </div>
                        </div>

                        <Link to="/notifications">
                          <div className="border-t px-4 py-3 text-center">
                            <button
                              onClick={() => {
                                setNfDropdownOpen(false);
                                navigate(`/notifications`);
                              }}
                              className="text-green-600 hover:text-green-700 hover:underline text-sm font-medium transition-colors duration-200"
                            >
                              {t("viewAll")}
                            </button>
                          </div>
                        </Link>
                      </div>
                    )}
                  </div>
                )}

                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setIsDropdownOpen((prev) => !prev)}
                    className="flex items-center focus:outline-none"
                  >
                    {user.avatar ? (
                      <img
                        src={`${user.avatar_url}`}
                        alt="avatar"
                        className="h-12 w-12 rounded-full"
                      />
                    ) : (
                      <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-6 w-6 text-gray-600"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 
                          9a7 7 0 1114 0H3z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                    )}
                    <svg
                      className={`ml-1 w-5 h-5 transform transition-transform ${isDropdownOpen ? "rotate-180" : ""
                        }`}
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {isDropdownOpen && (
                    <div
                      className={`absolute ${currentLang === "en" ? "right-0" : "left-0"
                        } mt-2 w-48 bg-white dark:bg-slate-700 dark:text-white rounded-md shadow-lg z-20`}
                    >
                      <p
                        dir={currentLang === "ar" ? "rtl" : "ltr"}
                        className="focus:outline-none text-gray-800 dark:text-white text-base leading-1 m-2 px-2 py-2 font-bold truncate max-w-[140px]"
                        style={{
                          direction: currentLang === "ar" ? "rtl" : "ltr",
                          unicodeBidi: "plaintext",
                        }}
                        title={user?.name || "User"}
                      >
                        {user?.name || "User"}
                      </p>

                      <div className="border-t"></div>
                      {filteredAccountMenu.map((item) =>
                        item?.download ? (
                          <a
                            className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-600"
                            key={item.label}
                            href={item.path}
                            download
                          >
                            {item.label}
                          </a>
                        ) : (
                          <Link
                            key={item.path}
                            to={item.path}
                            onClick={() => setIsDropdownOpen(false)}
                            className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-600"
                          >
                            {item.label}
                          </Link>
                        )
                      )}
                      <div className="border-t"></div>
                      <button
                        onClick={() => {
                          dispatch(logout());
                          setIsDropdownOpen(false);
                        }}
                        className="w-45 m-2 rounded-md p-2 text-md text-red-600 bg-red-100 hover:bg-red-600 hover:text-white"
                      >
                        {t("logout")}
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="flex items-center space-x-2">
                {/* <Link
                  to="/register"
                  className="px-5 py-2 rounded-full border border-green-700 text-green-700 hover:bg-green-50 transition"
                >
                  {t("register")}
                </Link> */}
                <Link
                  to="/login"
                  className="flex items-center space-x-2 px-5 py-2 rounded bg-green-700 text-white hover:bg-green-800 transition"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span>{t("login")}</span>
                </Link>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Mobile Navigation */}
      <nav className="lg:hidden bg-white dark:bg-slate-800 dark:text-white">
          <div
            className="fixed top-0 py-3 px-5 w-full flex justify-between items-center bg-white shadow-lg z-40 pt-0 mt-0 bg-white dark:bg-slate-800 dark:text-white"
            style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 0.75rem)" }}
          >
            <Link to={logoTarget} aria-label="logo" className="flex items-center">
              <img src="/logo.png" alt="Tarh Logo" className="h-15 w-auto" />
            </Link>
            <div className="flex items-center space-x-4">
              {/* Language Switcher Mobile */}
              <button
                onClick={switchLang}
                className="flex items-center gap-1 px-2 py-1 mx-1 rounded focus:outline-none hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                <span
                  className="text-lg font-bold"
                  style={{ fontFamily: "Tajawal', sans-serif !important" }}
                >
                  {currentLang === "ar" ? "EN" : "عربي"}
                </span>
              </button>

              <button
                id="menu"
                aria-label="open menu"
                className="focus:outline-none focus:ring-2 focus:ring-gray-600 rounded-md text-gray-800 dark:bg-slate-800 dark:text-white"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="icon icon-tabler icon-tabler-menu-2"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                  <line x1="4" y1="6" x2="20" y2="6" />
                  <line x1="4" y1="12" x2="20" y2="12" />
                  <line x1="4" y1="18" x2="20" y2="18" />
                </svg>
              </button>
            </div>
          </div>
        {/* Mobile Sidebar */}
        {isMobileMenuOpen && (
          <div className="fixed inset-0 z-40 lg:hidden ">
            {/* Overlay */}
            <div
              className="bg-gray-800 opacity-50 w-full h-full dark:bg-slate-700 dark:text-white"
              onClick={() => setIsMobileMenuOpen(false)}
            ></div>

            {/* Sidebar */}
            <div
              className="w-64 fixed top-0 left-0 h-screen bg-white shadow z-50 flex flex-col  transition duration-150 ease-in-out dark:bg-slate-800 dark:text-white"
              style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
            >
              <div className="flex-1 overflow-y-auto px-6">
                <div>
                  {/* Header */}
                  <div className="mt-6 flex w-full items-center justify-between">
                    <Link to={logoTarget} onClick={() => setIsMobileMenuOpen(false)}>
                      <img
                        src="/logo.png"
                        alt="Tarh Logo"
                        className="h-15 w-auto"
                      />
                    </Link>
                    <button
                      id="cross"
                      aria-label="close menu"
                      className="focus:outline-none focus:ring-2 rounded-md text-gray-800 dark:bg-slate-800 dark:text-white"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6 dark:bg-slate-800 dark:text-white"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path stroke="none" d="M0 0h24v24H0z"></path>
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                      </svg>
                    </button>
                  </div>

                  {/* Menu items */}
                  <ul className="mt-6">
                    {navbarMenus.map((item, idx) => (
                      <li key={`mobile-menu-${idx}-${item.path}`}>
                        {item.disabled ? (
                          <span
                            className="cursor-not-allowed text-gray-400 py-3 px-3 block dark:bg-slate-800 dark:text-gray-500"
                            title={getDisabledTitle(item.disabledReason)}
                          >
                            {item.label}
                            {item.disabledReason === "soon" && (
                              <span className="ml-1 text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 border border-gray-300 uppercase tracking-wide">
                                {t("soon")}
                              </span>
                            )}
                          </span>
                        ) : Array.isArray(item.children) ? (
                          <div className="py-1">
                            <button
                              type="button"
                              className="w-full cursor-pointer text-gray-800 py-3 px-3 block hover:bg-gray-100 dark:bg-slate-800 dark:text-white dark:hover:bg-gray-600 text-left font-bold"
                              onClick={() =>
                                setMobileOpenMenuKey((prev) =>
                                  prev === item.path ? null : item.path
                                )
                              }
                            >
                              {item.label}
                            </button>
                            {mobileOpenMenuKey === item.path && (
                              <div className="ms-3 border-s border-gray-200 dark:border-slate-700">
                                {(item.children || []).map((child) =>
                                  child.disabled ? (
                                    <span
                                      key={child.path}
                                      className="cursor-not-allowed text-gray-400 py-2 px-3 block dark:text-gray-500 font-bold"
                                      title={getDisabledTitle(child.disabledReason)}
                                    >
                                      {child.label}
                                      {child.disabledReason === "soon" && (
                                        <span className="ml-1 text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 border border-gray-300 uppercase tracking-wide">
                                          {t("soon")}
                                        </span>
                                      )}
                                    </span>
                                  ) : (
                                    <Link
                                      key={child.path}
                                      to={child.path}
                                      className="cursor-pointer text-gray-800 py-2 px-3 block hover:bg-gray-100 dark:bg-slate-800 dark:text-white dark:hover:bg-gray-600 font-bold"
                                      onClick={() => {
                                        setMobileOpenMenuKey(null);
                                        setIsMobileMenuOpen(false);
                                      }}
                                    >
                                      {child.label}
                                    </Link>
                                  )
                                )}
                              </div>
                            )}
                          </div>
                        ) : (
                          <Link
                            to={item.path}
                            className="cursor-pointer text-gray-800 py-3 px-3 block hover:bg-gray-100 dark:bg-slate-800 dark:text-white dark:hover:bg-gray-600"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            {item.label}
                          </Link>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Footer */}
                <div className="border-t border-gray-300 pt-4 dark:bg-slate-800 dark:text-white">
                  {user ? (
                    <div className="flex flex-col w-full dark:bg-slate-800 dark:text-white">
                      {/* Profile Row with Dropdown Toggle */}
                      <button
                        onClick={() => setIsMobileDropdownOpen((prev) => !prev)}
                        className="flex items-center justify-between w-full dark:bg-slate-800 dark:text-white"
                      >
                        <div className="flex items-center space-x-2 dark:bg-slate-800 dark:text-white">
                          {user.avatar ? (
                            <img
                              src={user.avatar_url}
                              alt="avatar"
                              className="h-12 w-12 rounded-full"
                            />
                          ) : (
                            <div className="h-10 w-10 bg-gray-200 rounded-full flex items-center justify-center dark:bg-slate-800 dark:text-white">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-5 w-5 text-gray-500 dark:bg-slate-800 dark:text-white"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            </div>
                          )}
                          <p
                            className="text-gray-800 text-base truncate max-w-[120px]"
                            title={user?.name || "User"}
                          >
                            {user?.name || "User"}
                          </p>
                        </div>

                        {/* Arrow Icon */}
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className={`h-5 w-5 text-gray-600 transform transition-transform dark:bg-slate-800 dark:text-white ${isMobileDropdownOpen ? "rotate-180" : ""
                            }`}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </button>

                      {/* Dropdown menu */}
                      {isMobileDropdownOpen && (
                        <div className="mt-3 flex flex-col border rounded-lg overflow-hidden dark:bg-slate-800 dark:text-white">
                          {filteredAccountMenu.map((item) =>
                            item?.download ? (
                              <a
                                key={item.label}
                                href={item.path}
                                download
                                onClick={() => {
                                  setIsMobileDropdownOpen(false);
                                  setIsMobileMenuOpen(false);
                                }}
                                className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                              >
                                {item.label}
                              </a>
                            ) : (
                              <Link
                                key={item.path}
                                to={item.path}
                                onClick={() => {
                                  setIsMobileDropdownOpen(false);
                                  setIsMobileMenuOpen(false);
                                }}
                                className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                              >
                                {item.label}
                              </Link>
                            )
                          )}
                          <button
                            onClick={() => {
                              dispatch(logout());
                              setIsMobileMenuOpen(false);
                              setIsMobileDropdownOpen(false);
                            }}
                            className="flex items-center px-4 py-2 text-sm text-red-600 hover:bg-gray-100 "
                          >
                            {t("logout")}
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <Link
                      to="/login"
                      className="flex items-center space-x-2 text-white font-medium py-2 px-6 rounded transition-colors duration-200 w-full justify-center "
                      style={{ backgroundColor: "#1B8354" }}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span>{t("login")}</span>
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </nav>
    </>
  );
}
